'use client'

import { BodyText, DetailText, HeadlineText, LeftCaretIcon, Modal, XIcon } from 'design-configuration/components'
import { Button } from '@components/button'
import { motion } from 'motion/react'
import React, { useState } from 'react'
import { trackButtonClicked, TrackedButtonNames } from '@utils/analytics'
import { cn } from '@/lib/utils'

export enum FilterPanelAnimation {
  SlideLeft = 'slide-left',
  FadeIn = 'fade-in'
}

type FilterPanelProps = {
  filterLayout: React.ReactElement
  filterOpen: boolean
  itemCount: number
  numActiveFilters: number
  clearFilter: () => void
  onFilterClose: () => void
  animationVariant?: FilterPanelAnimation
}

const animationVariants = {
  'slide-left': {
    initial: { opacity: 0, x: -350 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -350 },
    transition: { duration: 0.4 }
  },
  'fade-in': {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3 }
  }
}

export const FilterPanel = (props: FilterPanelProps): React.ReactElement => {
  const variant = props.animationVariant || FilterPanelAnimation.SlideLeft
  const animation = animationVariants[variant]

  return (
    <>
      <motion.div
        key="filter-panel"
        layout
        className="bg-surface pr-md top-md scrollbar-hidden pb-md sticky z-20 hidden h-[100dvh] w-[300px] flex-col overflow-y-scroll md:flex"
        initial={animation.initial}
        animate={animation.animate}
        exit={animation.exit}
        transition={animation.transition}
      >
        {props.filterLayout}
      </motion.div>
      <div className="block md:hidden">
        <MobileFilterPanel {...props} />
      </div>
    </>
  )
}
const MobileFilterPanel = (props: FilterPanelProps) => {
  const windowWidth = typeof window !== undefined ? window.innerWidth : 0
  return (
    <Modal
      modalType="full"
      show={props.filterOpen && windowWidth < 768}
      dismissOnClickOutside
      onDismiss={props.onFilterClose}
    >
      <div className="bg-surface p-md h-full w-full overflow-y-scroll">
        <div className="flex items-center justify-between">
          <HeadlineText className="font-light">Filters</HeadlineText>
          <button>
            <XIcon className="w-md h-md fill-on-surface-variant" onClick={props.onFilterClose} />
          </button>
        </div>
        <div className="mt-md mb-xl overflow-y-scroll">{props.filterLayout}</div>
      </div>
      <motion.div
        layout
        className="gap-x-xs py-sm px-md bg-surface absolute bottom-0 flex h-fit w-full justify-center shadow-[0_-1px_27px_0px_rgba(0,0,0,0.1)]"
      >
        <Button
          colorTheme={'primary'}
          className="w-full"
          onClick={() => props.clearFilter()}
          disabled={props.numActiveFilters === 0}
        >
          Clear all
        </Button>
        <Button colorTheme={'primary'} className="w-full" onClick={() => props.onFilterClose()}>
          Show results
        </Button>
      </motion.div>
    </Modal>
  )
}

export interface Option {
  label: string
  value: string
  disabled?: boolean
}

export type FilterOption = {
  name: string
  selectedOptions: string[]
  optionCounts?: Record<string, number>
  options: Option[]
  onChange: (value: string, selected: boolean) => void
  hideSelectedOptions?: boolean
  defaultOpen?: boolean
}

export const CommonFilterDropdown = ({
  name,
  optionCounts,
  options,
  selectedOptions,
  onChange,
  hideSelectedOptions,
  defaultOpen
}: FilterOption): React.ReactNode => {
  const [open, setOpen] = useState(defaultOpen || false)
  const isSelected = (option: string) => selectedOptions.includes(option)
  if (!options.length) return null

  return (
    <motion.div className="gap-y-xs py-md border-outline-variant flex w-full flex-col border-b" layout>
      <motion.button
        className="text-on-surface flex w-full items-center justify-between text-left"
        onClick={() => setOpen(!open)}
        layout
      >
        <BodyText>{name}</BodyText>
        <div className="gap-x-xs flex items-center">
          {!hideSelectedOptions && selectedOptions.length ? (
            <div className="border-outline-variant px-xs rounded-full border py-[2px]">
              <DetailText className="font-medium">{selectedOptions.length}</DetailText>
            </div>
          ) : null}
          <motion.div
            animate={{
              rotate: open ? 90 : 270
            }}
            transition={{ duration: 0.2 }}
          >
            <LeftCaretIcon className={`w-sm h-sm fill-on-surface`} />
          </motion.div>
        </div>
      </motion.button>
      {open ? (
        <motion.div
          layout
          className="gap-y-sm mt-md flex flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {options.map((option) =>
            !optionCounts || optionCounts?.[option.value] ? (
              <motion.button
                layout
                key={option.value}
                className={cn(
                  'p-xs rounded-xs hover:border-outline-variant flex w-full items-baseline justify-between border border-transparent text-left',
                  isSelected(option.value)
                    ? 'text-on-surface bg-surface-container border-outline-variant hover:border-outline'
                    : 'text-on-surface-variant'
                )}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                onClick={() => {
                  onChange(option.value, !isSelected(option.value))
                  trackButtonClicked(TrackedButtonNames.filterOption, {
                    customProperties: {
                      'Filter name': name,
                      option: option.value
                    }
                  })
                }}
              >
                <BodyText className="text-on-surface">{option.label}</BodyText>
                {optionCounts ? <BodyText>{optionCounts?.[option.value]}</BodyText> : null}
              </motion.button>
            ) : null
          )}
        </motion.div>
      ) : null}
    </motion.div>
  )
}

export const CommonFilterPanel = ({
  filters,
  className
}: {
  filters: (FilterOption | undefined)[]
  className?: string
}): React.ReactElement => {
  return (
    <motion.div layout className={cn('flex flex-col', className)}>
      {filters.map(
        (filter) => filter && <CommonFilterDropdown key={filter.name} defaultOpen={filters.length === 1} {...filter} />
      )}
    </motion.div>
  )
}
